// Factory — route /factory
//
// The studio hub (master-detail). Left: production lines (a line = a content
// profile + schedule settings). Right: the selected line's [Settings | Stories]
// tabs — settings edits the line + its profile inline; stories lists the line's
// runs with review→render. Absorbs the old Profiles + Stories pages.

import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Plus, Trash2 } from 'lucide-react'
import { api, ApiError } from '@/api/client'
import type { FactoryConfigInput } from '@/api/types'
import { Badge, Button, Card, EmptyState, Input, Select } from '@/admin-design/_common'
import FactoryLineSettings from '@/components/FactoryLineSettings'
import FactoryLineStories from '@/components/FactoryLineStories'

export default function Factory() {
  const qc = useQueryClient()
  const configsQ = useQuery({ queryKey: ['factory-configs'], queryFn: api.listFactoryConfigs })
  const profilesQ = useQuery({ queryKey: ['channel-profiles'], queryFn: api.listProfiles })

  // Selection lives in the URL (?line=&tab=&run=) so the story-selection screen
  // is a shareable/bookmarkable permalink and survives refresh.
  const [searchParams, setSearchParams] = useSearchParams()
  const lineParam = searchParams.get('line')
  const selectedId = lineParam ? Number(lineParam) : null
  const tab: 'settings' | 'stories' =
    searchParams.get('tab') === 'settings' ? 'settings' : 'stories'
  const [creating, setCreating] = useState(false)

  const setSelectedId = (id: number | null) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        if (id == null) {
          next.delete('line')
          next.delete('run')
        } else {
          next.set('line', String(id))
        }
        return next
      },
      { replace: true },
    )
  }
  const setTab = (t: 'settings' | 'stories') => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        next.set('tab', t)
        if (t !== 'stories') next.delete('run')
        return next
      },
      { replace: true },
    )
  }

  const invalidate = () => qc.invalidateQueries({ queryKey: ['factory-configs'] })
  const createM = useMutation({
    mutationFn: (input: FactoryConfigInput) => api.createFactoryConfig(input),
    onSuccess: (c) => {
      setCreating(false)
      setSelectedId(c.id)
      invalidate()
    },
  })
  const deleteM = useMutation({
    mutationFn: (id: number) => api.deleteFactoryConfig(id),
    onSuccess: () => {
      setSelectedId(null)
      invalidate()
    },
  })

  const configs = configsQ.data ?? []
  const profiles = profilesQ.data ?? []
  const profileName = (id: number) => profiles.find((p) => p.id === id)?.name ?? `#${id}`
  const selected = configs.find((c) => c.id === selectedId) ?? null

  return (
    <div className="space-y-4">
      <header className="flex items-center gap-3">
        <h1 className="text-xl font-semibold text-foreground">Factory</h1>
        <span className="text-sm text-muted-foreground">
          {configs.length}개 라인 · 스토리·프로필 통합
        </span>
        <Button
          type="button"
          variant="primary"
          size="sm"
          className="ml-auto"
          onClick={() => setCreating((v) => !v)}
        >
          <Plus size={14} /> 라인 추가
        </Button>
      </header>

      {creating && (
        <ConfigForm
          profiles={profiles}
          busy={createM.isPending}
          error={createM.isError ? errorMessage(createM.error) : null}
          onCancel={() => setCreating(false)}
          onSubmit={(input) => createM.mutate(input)}
        />
      )}

      {/* Master-detail */}
      <div className="grid gap-4 lg:grid-cols-[260px_1fr]">
        {/* Lines */}
        <Card className="overflow-hidden">
          {configs.length === 0 ? (
            <EmptyState
              title="라인이 없어요."
              description="프로필별로 자동 생성 라인을 만들어보세요."
            />
          ) : (
            <ul className="divide-y divide-border">
              {configs.map((c) => (
                <li key={c.id}>
                  <button
                    type="button"
                    onClick={() => setSelectedId(c.id)}
                    className={`flex w-full flex-col gap-0.5 px-3 py-2 text-left transition ${selectedId === c.id ? 'bg-muted/50' : 'hover:bg-muted/30'}`}
                  >
                    <span className="flex items-center gap-2 text-sm font-medium text-card-foreground">
                      {c.name}
                      <Badge
                        variant={c.enabled ? 'success' : 'muted'}
                        className="rounded text-[10px]"
                      >
                        {c.enabled ? 'ON' : 'OFF'}
                      </Badge>
                    </span>
                    <span className="text-[11px] text-muted-foreground">
                      {profileName(c.channel_profile_id)} · {c.daily_target}/일 · {c.render_mode}
                      {c.review_gate ? ' · 검토' : ''}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </Card>

        {/* Detail */}
        <div className="min-w-0">
          {!selected ? (
            <Card>
              <div className="p-6 text-sm text-muted-foreground">왼쪽에서 라인을 선택하세요.</div>
            </Card>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <div className="flex rounded-md border border-border p-0.5">
                  {(['stories', 'settings'] as const).map((t) => (
                    <button
                      key={t}
                      type="button"
                      onClick={() => setTab(t)}
                      className={`rounded px-3 py-1 text-xs font-medium transition ${tab === t ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                    >
                      {t === 'stories' ? '스토리' : '설정'}
                    </button>
                  ))}
                </div>
                <span className="text-sm font-semibold text-foreground">{selected.name}</span>
                <button
                  onClick={() => deleteM.mutate(selected.id)}
                  className="ml-auto p-1 text-muted-foreground hover:text-destructive"
                  aria-label="delete line"
                  disabled={deleteM.isPending}
                >
                  <Trash2 size={14} />
                </button>
              </div>

              {tab === 'stories' ? (
                <FactoryLineStories config={selected} />
              ) : (
                <FactoryLineSettings config={selected} />
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────

function ConfigForm({
  profiles,
  busy,
  error,
  onSubmit,
  onCancel,
}: {
  profiles: { id: number; name: string }[]
  busy: boolean
  error: string | null
  onSubmit: (input: FactoryConfigInput) => void
  onCancel: () => void
}) {
  const [name, setName] = useState('')
  const [profileId, setProfileId] = useState<number | null>(profiles[0]?.id ?? null)
  const [dailyTarget, setDailyTarget] = useState(1)
  const [mode, setMode] = useState<'fast' | 'full'>('fast')
  const [reviewGate, setReviewGate] = useState(false)
  const [storyFormat, setStoryFormat] = useState<'short' | 'narrative'>('narrative')
  const [targetLen, setTargetLen] = useState(60)
  const [minScenes, setMinScenes] = useState(0)

  const canSubmit = name.trim() !== '' && Boolean(profileId) && !busy

  return (
    <Card>
      <Card.Body className="space-y-3 p-4">
        <div className="grid min-w-0 gap-3 sm:grid-cols-2">
          <div className="min-w-0">
            <div className="mb-1 text-[11px] text-muted-foreground">라인 이름</div>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="예: 사연 라디오 자동"
            />
          </div>
          <div className="min-w-0">
            <div className="mb-1 text-[11px] text-muted-foreground">프로필</div>
            <Select
              value={profileId ? String(profileId) : ''}
              onValueChange={(v) => setProfileId(Number(v))}
              placeholder="프로필 선택"
              options={profiles.map((p) => ({ label: p.name, value: String(p.id) }))}
            />
          </div>
          <div className="min-w-0">
            <div className="mb-1 text-[11px] text-muted-foreground">하루 목표 개수</div>
            <Input
              type="number"
              min={1}
              value={dailyTarget}
              onChange={(e) => setDailyTarget(Number(e.target.value))}
            />
          </div>
          <div className="min-w-0">
            <div className="mb-1 text-[11px] text-muted-foreground">렌더 모드</div>
            <Select
              value={mode}
              onValueChange={(v) => setMode(v as 'fast' | 'full')}
              options={[
                { label: 'fast (프리뷰)', value: 'fast' },
                { label: 'full (2K)', value: 'full' },
              ]}
            />
          </div>
          <div className="min-w-0">
            <div className="mb-1 text-[11px] text-muted-foreground">스토리 포맷</div>
            <div className="flex rounded-md border border-border p-0.5">
              {(['narrative', 'short'] as const).map((f) => (
                <button
                  key={f}
                  type="button"
                  onClick={() => setStoryFormat(f)}
                  className={
                    'flex-1 rounded px-2 py-1 text-xs font-medium transition ' +
                    (storyFormat === f
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:text-foreground')
                  }
                >
                  {f}
                </button>
              ))}
            </div>
          </div>
          <div className="min-w-0">
            <div className="mb-1 text-[11px] text-muted-foreground">목표 길이(초)</div>
            <Input
              type="number"
              min={0}
              value={targetLen}
              onChange={(e) => setTargetLen(Number(e.target.value))}
            />
          </div>
          <div className="min-w-0">
            <div className="mb-1 text-[11px] text-muted-foreground">최소 씬 수 (0=자동)</div>
            <Input
              type="number"
              min={0}
              value={minScenes}
              onChange={(e) => setMinScenes(Number(e.target.value))}
            />
          </div>
        </div>
        <label className="flex items-center gap-2 text-sm text-foreground">
          <input
            type="checkbox"
            checked={reviewGate}
            onChange={(e) => setReviewGate(e.target.checked)}
            className="h-4 w-4 rounded border-border"
          />
          검토 후 렌더 (반자동) — 스토리 생성 뒤 멈추고, 스토리 탭에서 [진행]
        </label>
        {error && <p className="text-sm text-destructive">{error}</p>}
        <div className="flex justify-end gap-2 border-t border-border pt-2">
          <Button type="button" variant="secondary" size="sm" onClick={onCancel} disabled={busy}>
            취소
          </Button>
          <Button
            type="button"
            variant="primary"
            size="sm"
            disabled={!canSubmit}
            loading={busy}
            onClick={() =>
              onSubmit({
                name: name.trim(),
                channel_profile_id: profileId ?? 0,
                daily_target: dailyTarget > 0 ? dailyTarget : 1,
                render_mode: mode,
                default_visibility: 'private',
                enabled: true,
                review_gate: reviewGate,
                story_format: storyFormat,
                target_length_sec: targetLen >= 0 ? targetLen : 60,
                min_scenes: minScenes >= 0 ? minScenes : 0,
                tags: '',
                select_mode: 'tag',
                min_score: 0,
                spike_only: false,
              })
            }
          >
            추가
          </Button>
        </div>
      </Card.Body>
    </Card>
  )
}

function errorMessage(e: unknown): string {
  if (e instanceof ApiError) return e.message
  if (e instanceof Error) return e.message
  return 'unknown error'
}
